import React, { useEffect, useState } from 'react';
import 'styles/home.css';
import 'styles/contact.css';

import Request from 'files/Request.js';
import { campaign, trackEvent } from 'files/analytics.js';
import { SiteFooter, SiteNav } from 'views/SiteChrome.js';
import BotTrap, { trapValue } from 'views/BotTrap.js';

/* The contact page.

   This replaces a form left over from the old site, which asked a stranger for
   a phone number, a list of services and a project start date before it would
   accept anything. Those are questions for the second conversation.

   The copy holds both of the people who write to us, which the first draft of
   this page did not. "Tell us what is slowing you down" speaks to an owner
   already in pain and quietly tells a founder with something to build that this
   is not for them, along with every established company adding an offering. Same
   frame as the Quick Look: the distance between where a business is and where it
   could be, whether that distance is something broken or something not built.

   Two ways out of it, side by side and equally weighted: the form, and the
   address and phone number in plain text for anyone who would rather not use a
   form at all. Making the direct route look like the lesser option is how you
   end up with fewer of both.

   The one thing the copy has to do is say what happens next, specifically. */

const Contact = ({ utils }) => {

    const [enquiry, setEnquiry] = useState({ email: '', message: '', name: '', phone: '' });
    const [failure, setFailure] = useState(null);
    const [sending, setSending] = useState(false);
    const [sent, setSent] = useState(false);

    // the marketing pages own the ground while they are mounted
    useEffect(() => {
        document.body.classList.add('dl-body');
        return () => document.body.classList.remove('dl-body');
    }, []);

    const onChange = field => evt => setEnquiry({ ...enquiry, [field]: evt.target.value });

    const onSubmit = async evt => {

        evt.preventDefault();
        if(sending) return;
        const trap = trapValue(evt);

        setFailure(null);
        setSending(true);
        try {
            await Request.post(utils, '/utils/', {
                email_address: enquiry.email.trim(),
                message: enquiry.message.trim(),
                name: enquiry.name.trim(),
                phone_number: enquiry.phone.trim() || null,
                company_website: trap,
                source: 'contact',
                type: 'new_contact_form',

                // stored with the enquiry, not just counted in analytics — see
                // the note on the same field in QuickLook
                campaign: campaign()
            });
            setSending(false);
            setSent(true);
            trackEvent('contact-submitted', { from: 'contact' });

        } catch(e) {
            setSending(false);
            setFailure(`We couldn’t send that. ${e.message || 'Please try again'}, or email hello@duralabs.io directly.`);
        }
    }

    const disabled = sending || !enquiry.email.trim() || !enquiry.name.trim();

    return (
        <div className={'dl'}>
            <SiteNav utils={utils} />

            <section className={'ct'}>
                <div className={'dl-wrap'}>

                    <header className={'ct-head'}>
                        <h1 className={'dl-display'}>Tell us what you’re trying to do.</h1>
                        <p className={'ct-dek'}>
                            Something that isn’t working, or something you want to build and
                            haven’t. A sentence is enough to start, and if software isn’t the
                            right answer we’ll say so, and that costs you nothing.
                        </p>
                    </header>

                    <div className={'ct-grid'}>

                        <div className={'ct-say'}>
                            <h2>What happens after you send this</h2>
                            <p>
                                It comes to us, not to a queue. We read it, and we reply within a
                                business day with either a question or a straight answer about
                                whether this is something we can help with. That goes for fixing
                                something as much as building something new.
                            </p>
                            <p>
                                There’s no call to book and nothing to sit through. If it turns into
                                a conversation, that’s because you asked for one.
                            </p>
                        </div>

                        {sent ? (
                            <div className={'dl-form dl-form-done'}>
                                <h3>That’s with us.</h3>
                                <p>
                                    We read every one of these ourselves and reply within a business
                                    day. If it’s urgent, call us. The number is on the left.
                                </p>
                            </div>
                        ) : (
                            <form className={'dl-form'} onSubmit={onSubmit}>
                                <BotTrap />
                                <label className={'dl-field'}>
                                    <span>Your name</span>
                                    <input
                                    autoComplete={'name'}
                                    onChange={onChange('name')}
                                    type={'text'}
                                    value={enquiry.name} />
                                </label>
                                <label className={'dl-field'}>
                                    <span>Email</span>
                                    <input
                                    autoComplete={'email'}
                                    onChange={onChange('email')}
                                    type={'email'}
                                    value={enquiry.email} />
                                </label>
                                <label className={'dl-field'}>
                                    <span>Phone <i>Optional</i></span>
                                    <input
                                    autoComplete={'tel'}
                                    onChange={onChange('phone')}
                                    type={'tel'}
                                    value={enquiry.phone} />
                                </label>
                                <label className={'dl-field'}>
                                    <span>What are you trying to do?</span>
                                    <textarea
                                    onChange={onChange('message')}
                                    placeholder={'We want to offer our customers online booking and nothing we have will do it.'}
                                    rows={5}
                                    value={enquiry.message} />
                                </label>
                                {failure && <p className={'dl-form-error'}>{failure}</p>}
                                <button className={'dl-btn'} disabled={disabled} type={'submit'}>
                                    {sending ? 'Sending…' : 'Send it over'} <span className={'dl-arrow'}>→</span>
                                </button>
                            </form>
                        )}
                    </div>
                </div>
            </section>

            <SiteFooter utils={utils} />
        </div>
    )
}

export default Contact;
